// Grafieken van het BRES-designsystem: lijn, gestapelde kolommen, balkenlijst, scorering en sparkline.
// Regels (zie docs/design/BRES-DESIGN-SYSTEM.md, "Grafieken"): reekskleuren in vaste volgorde (--series-1..4),
// dunne markeringen, 2px oppervlaktespleet tussen gestapelde delen, één as, tekst nooit in reekskleur,
// legenda vanaf twee reeksen, hover met tooltip en altijd een (verborgen) tabel met dezelfde cijfers.
import { useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { cx } from "../cx.ts";

export type SeriesColor = 1 | 2 | 3 | 4 | "success" | "info" | "warning" | "danger" | "muted";

export function seriesVar(color: SeriesColor): string {
  if (typeof color === "number") return `var(--series-${color})`;
  if (color === "muted") return "var(--line-strong)";
  return `var(--${color})`;
}

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Ronde, leesbare asgrenzen: 0, 1, 2, 2.5, 5 × 10^n. */
export function niceMax(value: number, ticks = 4): { max: number; step: number } {
  if (!(value > 0)) return { max: ticks, step: 1 };
  const raw = value / ticks;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  return { max: step * Math.ceil(value / step), step };
}

export interface LegendItem {
  key: string;
  label: ReactNode;
  color: SeriesColor;
  shape?: "line" | "dot";
}

export function ChartLegend({ items, className }: { items: LegendItem[]; className?: string }) {
  if (items.length < 2) return null;
  return (
    <ul className={cx("ui-legend", className)}>
      {items.map((i) => (
        <li key={i.key}>
          <span className={cx("ui-legend__key", i.shape === "line" && "ui-legend__key--line")} style={{ background: seriesVar(i.color) }} aria-hidden />
          {i.label}
        </li>
      ))}
    </ul>
  );
}

interface Tip {
  x: number;
  y: number;
  content: ReactNode;
}

function Tooltip({ tip, width }: { tip: Tip | null; width: number }) {
  if (!tip) return null;
  const left = Math.min(Math.max(tip.x, 70), Math.max(70, width - 70));
  return (
    <div className="ui-chart-tip" style={{ left, top: tip.y }} role="presentation">
      {tip.content}
    </div>
  );
}

/** Dezelfde cijfers als tabel voor schermlezers. In een sr-only-div: een tabel zelf negeert de 1px-breedte en zou de pagina verbreden. */
function SrTable({ caption, head, rows }: { caption: string; head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="sr-only">
    <table>
      <caption>{caption}</caption>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (j === 0 ? <th key={j} scope="row">{c}</th> : <td key={j}>{c}</td>))}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

// ---------------------------------------------------------------- lijngrafiek (tijd)
export interface LineSeries {
  key: string;
  label: string;
  color: SeriesColor;
  points: { x: number; y: number }[];
}

export function LineChart({
  series,
  label,
  height = 220,
  yMax,
  formatY = (v) => String(v),
  formatX,
  formatTipX,
  area,
}: {
  series: LineSeries[];
  label: string;
  height?: number;
  yMax?: number;
  formatY?: (v: number) => string;
  formatX: (x: number) => string;
  formatTipX?: (x: number) => string;
  area?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const id = useId();
  const pad = { l: 40, r: 14, t: 12, b: 28 };
  const xs = useMemo(() => [...new Set(series.flatMap((s) => s.points.map((p) => p.x)))].sort((a, b) => a - b), [series]);
  const maxY = yMax ?? niceMax(Math.max(0, ...series.flatMap((s) => s.points.map((p) => p.y)))).max;
  const { step } = yMax ? { step: yMax / 4 } : niceMax(maxY);
  const x0 = xs[0] ?? 0;
  const x1 = xs.length > 1 ? xs.at(-1)! : x0 + 86_400_000;
  const span = x1 - x0 || 1;
  const innerW = Math.max(10, width - pad.l - pad.r);
  const innerH = height - pad.t - pad.b;
  const sx = (x: number) => pad.l + (xs.length > 1 ? ((x - x0) / span) * innerW : innerW / 2);
  const sy = (y: number) => pad.t + innerH - (Math.min(y, maxY) / maxY) * innerH;
  const yTicks = Array.from({ length: Math.round(maxY / step) + 1 }, (_, i) => i * step);
  const xTickCount = Math.max(2, Math.min(6, Math.floor(innerW / 110)));
  const xTicks = xs.length > 1 ? Array.from({ length: xTickCount }, (_, i) => x0 + (span * i) / (xTickCount - 1)) : xs;

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - box.left + pad.l;
    let best = 0;
    xs.forEach((x, i) => {
      if (Math.abs(sx(x) - px) < Math.abs(sx(xs[best]!) - px)) best = i;
    });
    setHover(xs.length ? best : null);
  };
  const hx = hover !== null ? xs[hover] : undefined;
  const tip: Tip | null =
    hx !== undefined
      ? {
          x: sx(hx),
          y: pad.t,
          content: (
            <>
              <div className="ui-chart-tip__title">{(formatTipX ?? formatX)(hx)}</div>
              {series.map((s) => {
                const p = s.points.find((q) => q.x === hx);
                return p ? (
                  <div key={s.key} className="ui-chart-tip__row">
                    <span className="ui-legend__key ui-legend__key--line" style={{ background: seriesVar(s.color) }} aria-hidden />
                    <span>{s.label}</span>
                    <strong>{formatY(p.y)}</strong>
                  </div>
                ) : null;
              })}
            </>
          ),
        }
      : null;

  return (
    <figure className="ui-chart" aria-label={label}>
      <ChartLegend items={series.map((s) => ({ key: s.key, label: s.label, color: s.color, shape: "line" }))} />
      <div className="ui-chart__plot" ref={ref} style={{ height }}>
        {width > 0 ? (
          <svg width={width} height={height} aria-hidden>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`${id}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor={seriesVar(s.color)} stopOpacity="0.16" />
                  <stop offset="1" stopColor={seriesVar(s.color)} stopOpacity="0" />
                </linearGradient>
              ))}
            </defs>
            {yTicks.map((v) => (
              <g key={v}>
                <line className="ui-chart__grid" x1={pad.l} x2={width - pad.r} y1={sy(v)} y2={sy(v)} />
                <text className="ui-chart__tick" x={pad.l - 8} y={sy(v)} dy="0.32em" textAnchor="end">
                  {formatY(v)}
                </text>
              </g>
            ))}
            {xTicks.map((x, i) =>
              i > 0 && formatX(x) === formatX(xTicks[i - 1]!) ? null : (
                <text key={i} className="ui-chart__tick" x={sx(x)} y={height - 8} textAnchor={i === 0 && xs.length > 1 ? "start" : i === xTicks.length - 1 && xs.length > 1 ? "end" : "middle"}>
                  {formatX(x)}
                </text>
              ),
            )}
            {series.map((s) => {
              const pts = [...s.points].sort((a, b) => a.x - b.x);
              if (!pts.length) return null;
              const d = pts.map((p, i) => `${i ? "L" : "M"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join("");
              const last = pts.at(-1)!;
              return (
                <g key={s.key}>
                  {area && pts.length > 1 ? (
                    <path className="ui-chart__area" d={`${d}L${sx(last.x).toFixed(1)},${sy(0)}L${sx(pts[0]!.x).toFixed(1)},${sy(0)}Z`} fill={`url(#${id}-${s.key})`} />
                  ) : null}
                  {pts.length > 1 ? <path className="ui-chart__line" d={d} stroke={seriesVar(s.color)} pathLength={1} /> : null}
                  <circle className="ui-chart__dot" cx={sx(last.x)} cy={sy(last.y)} r={4} fill={seriesVar(s.color)} />
                </g>
              );
            })}
            {hx !== undefined ? (
              <g>
                <line className="ui-chart__cross" x1={sx(hx)} x2={sx(hx)} y1={pad.t} y2={pad.t + innerH} />
                {series.map((s) => {
                  const p = s.points.find((q) => q.x === hx);
                  return p ? <circle key={s.key} className="ui-chart__dot" cx={sx(p.x)} cy={sy(p.y)} r={5} fill={seriesVar(s.color)} /> : null;
                })}
              </g>
            ) : null}
            <rect className="ui-chart__hit" x={pad.l} y={0} width={innerW} height={height} onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
          </svg>
        ) : null}
        <Tooltip tip={tip} width={width} />
      </div>
      <SrTable
        caption={label}
        head={["", ...series.map((s) => s.label)]}
        rows={xs.map((x) => [(formatTipX ?? formatX)(x), ...series.map((s) => {
          const p = s.points.find((q) => q.x === x);
          return p ? formatY(p.y) : "";
        })])}
      />
    </figure>
  );
}

// ---------------------------------------------------------------- gestapelde kolommen (perioden)
export interface ColumnSeries {
  key: string;
  label: string;
  color: SeriesColor;
}

export function ColumnChart({
  categories,
  series,
  values,
  label,
  height = 200,
  formatY = (v) => String(v),
}: {
  categories: { key: string; label: string; tipLabel?: string }[];
  series: ColumnSeries[];
  values: Record<string, Record<string, number>>;
  label: string;
  height?: number;
  formatY?: (v: number) => string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { l: 36, r: 6, t: 12, b: 26 };
  const totals = categories.map((c) => series.reduce((sum, s) => sum + (values[c.key]?.[s.key] ?? 0), 0));
  const { max, step } = niceMax(Math.max(0, ...totals));
  const innerW = Math.max(10, width - pad.l - pad.r);
  const innerH = height - pad.t - pad.b;
  const band = innerW / Math.max(1, categories.length);
  const barW = Math.max(4, Math.min(24, band * 0.62));
  const sy = (v: number) => (v / max) * innerH;
  const base = pad.t + innerH;
  const ticks = Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step);
  const labelEvery = Math.max(1, Math.ceil(categories.length / Math.max(2, Math.floor(innerW / 56))));
  const hc = hover !== null ? categories[hover] : undefined;

  return (
    <figure className="ui-chart" aria-label={label}>
      <ChartLegend items={series.map((s) => ({ key: s.key, label: s.label, color: s.color }))} />
      <div className="ui-chart__plot" ref={ref} style={{ height }}>
        {width > 0 ? (
          <svg width={width} height={height} aria-hidden>
            {ticks.map((v) => (
              <g key={v}>
                <line className="ui-chart__grid" x1={pad.l} x2={width - pad.r} y1={base - sy(v)} y2={base - sy(v)} />
                <text className="ui-chart__tick" x={pad.l - 8} y={base - sy(v)} dy="0.32em" textAnchor="end">
                  {formatY(v)}
                </text>
              </g>
            ))}
            {categories.map((c, i) => {
              const cx0 = pad.l + band * i + band / 2;
              let acc = 0;
              const parts = series
                .map((s) => ({ s, v: values[c.key]?.[s.key] ?? 0 }))
                .filter((p) => p.v > 0);
              return (
                <g key={c.key} className="ui-chart__col" style={{ ["--i" as string]: i } as CSSProperties} data-active={hover === i || undefined}>
                  {hover === i ? <rect className="ui-chart__band" x={pad.l + band * i} y={pad.t} width={band} height={innerH} /> : null}
                  {parts.map((p, j) => {
                    const h = sy(p.v);
                    const y = base - sy(acc) - h;
                    acc += p.v;
                    const top = j === parts.length - 1;
                    const gap = j > 0 ? 2 : 0;
                    const r = top ? Math.min(4, h / 2) : 0;
                    const x = cx0 - barW / 2;
                    const hh = Math.max(0, h - gap);
                    const d = top
                      ? `M${x},${y + hh}V${y + r}Q${x},${y} ${x + r},${y}H${x + barW - r}Q${x + barW},${y} ${x + barW},${y + r}V${y + hh}Z`
                      : `M${x},${y + hh}V${y}H${x + barW}V${y + hh}Z`;
                    return <path key={p.s.key} d={d} fill={seriesVar(p.s.color)} />;
                  })}
                  {i % labelEvery === 0 ? (
                    <text className="ui-chart__tick" x={cx0} y={height - 8} textAnchor="middle">
                      {c.label}
                    </text>
                  ) : null}
                  <rect className="ui-chart__hit" x={pad.l + band * i} y={0} width={band} height={height} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)} />
                </g>
              );
            })}
          </svg>
        ) : null}
        <Tooltip
          width={width}
          tip={
            hc
              ? {
                  x: pad.l + band * hover! + band / 2,
                  y: pad.t,
                  content: (
                    <>
                      <div className="ui-chart-tip__title">{hc.tipLabel ?? hc.label}</div>
                      {series.map((s) => (
                        <div key={s.key} className="ui-chart-tip__row">
                          <span className="ui-legend__key" style={{ background: seriesVar(s.color) }} aria-hidden />
                          <span>{s.label}</span>
                          <strong>{formatY(values[hc.key]?.[s.key] ?? 0)}</strong>
                        </div>
                      ))}
                    </>
                  ),
                }
              : null
          }
        />
      </div>
      <SrTable caption={label} head={["", ...series.map((s) => s.label)]} rows={categories.map((c) => [c.tipLabel ?? c.label, ...series.map((s) => formatY(values[c.key]?.[s.key] ?? 0))])} />
    </figure>
  );
}

// ---------------------------------------------------------------- balkenlijst (horizontaal, optioneel gestapeld)
export interface BarRow {
  key: string;
  label: ReactNode;
  /** Tekst voor tooltip en tabel als label geen tekst is. */
  text?: string;
  meta?: ReactNode;
  value: number;
  valueLabel?: string;
  segments?: { key: string; label: string; value: number; color: SeriesColor }[];
  color?: SeriesColor;
}

export function BarList({ rows, label, max, legend }: { rows: BarRow[]; label: string; max?: number; legend?: LegendItem[] }) {
  const top = max ?? Math.max(1, ...rows.map((r) => r.value));
  return (
    <figure className="ui-chart" aria-label={label}>
      {legend ? <ChartLegend items={legend} /> : null}
      <ul className="ui-barlist">
        {rows.map((r, i) => {
          const segs = r.segments ?? [{ key: "v", label: r.text ?? "", value: r.value, color: r.color ?? 1 }];
          const title = segs.length > 1 ? segs.map((s) => `${s.label}: ${s.value}`).join(" · ") : undefined;
          return (
            <li key={r.key} style={{ ["--i" as string]: i } as CSSProperties}>
              <div className="ui-barlist__head">
                <span className="ui-barlist__label">{r.label}</span>
                {r.meta ? <span className="ui-barlist__meta">{r.meta}</span> : null}
                <span className="ui-barlist__value">{r.valueLabel ?? r.value}</span>
              </div>
              <div className="ui-barlist__track" title={title}>
                {segs
                  .filter((s) => s.value > 0)
                  .map((s) => (
                    <span key={s.key} className="ui-barlist__seg" style={{ width: `${(s.value / top) * 100}%`, background: seriesVar(s.color) }} />
                  ))}
              </div>
            </li>
          );
        })}
      </ul>
      <SrTable caption={label} head={["", ...(rows[0]?.segments?.map((s) => s.label) ?? [label])]} rows={rows.map((r) => [r.text ?? String(r.key), ...(r.segments ? r.segments.map((s) => s.value) : [r.valueLabel ?? r.value])])} />
    </figure>
  );
}

// ---------------------------------------------------------------- scorering (Lighthouse 0-100)
/** Lighthouse-drempels: 90-100 goed, 50-89 matig, 0-49 slecht. Het getal staat altijd in de ring. */
export function scoreTone(score: number): "success" | "warning" | "danger" {
  return score >= 90 ? "success" : score >= 50 ? "warning" : "danger";
}

export function ScoreRing({ value, label, size = 72 }: { value: number | null; label: string; size?: number }) {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const tone = value === null ? null : scoreTone(value);
  return (
    <div className={cx("ui-score", tone && `ui-score--${tone}`)} style={{ ["--size" as string]: `${size}px` } as CSSProperties}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle className="ui-score__track" cx={size / 2} cy={size / 2} r={r} />
        {value !== null ? (
          <circle className="ui-score__fill" cx={size / 2} cy={size / 2} r={r} strokeDasharray={`${(value / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        ) : null}
      </svg>
      <span className="ui-score__value num">{value ?? "–"}</span>
      <span className="ui-score__label">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------- sparkline
export function Sparkline({ values, color = 1, label, height = 32 }: { values: number[]; color?: SeriesColor; label: string; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const id = useId();
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => [values.length > 1 ? (i / (values.length - 1)) * (width - 6) + 3 : width / 2, height - 3 - (v / max) * (height - 8)] as const);
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
  return (
    <div className="ui-spark" ref={ref} style={{ height }} role="img" aria-label={label}>
      {width > 0 && pts.length ? (
        <svg width={width} height={height} aria-hidden>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={seriesVar(color)} stopOpacity="0.18" />
              <stop offset="1" stopColor={seriesVar(color)} stopOpacity="0" />
            </linearGradient>
          </defs>
          {pts.length > 1 ? <path d={`${d}L${pts.at(-1)![0]},${height}L${pts[0]![0]},${height}Z`} fill={`url(#${id})`} /> : null}
          {pts.length > 1 ? <path className="ui-chart__line" d={d} stroke={seriesVar(color)} pathLength={1} /> : null}
          <circle className="ui-chart__dot" cx={pts.at(-1)![0]} cy={pts.at(-1)![1]} r={3} fill={seriesVar(color)} />
        </svg>
      ) : null}
    </div>
  );
}
